// v4 (spec-v4-structure-and-stage.md sections 3.4, 5.3 "Shot detail with References" and 9; ticket V4-R2): the
// References fold in a scene's detail. It shows the pictures the next frame will be sent (V4-R1's picker), in order,
// as thumbnails with a label and the reason, and lets the user swap one for another picture, pin it, take it out, add
// one from the candidates or the library (or upload a new one), and go back to automatic. Every tap is a free PUT;
// "Redo with these · 8" saves them (PUT) and makes the frame (POST), with the price on the button first.
//
// Never folded (5.2): the picker's warnings (too many characters with a model that takes more, a model that takes no
// pictures, a missing panel) sit above the fold as Notices, and the redo price sits on the fold's summary row.
// Phone first: a horizontal row of thumbnails inside the detail's gutter and a bottom sheet for swapping; on desktop
// the same fold sits in the suite's Scene column.
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import * as act from "../actions";
import { api, ApiError } from "../api";
import { director } from "../director";
import { openWorld } from "../world";
import { UPLOAD_TERMS_CONSENT } from "../legal/content";
import {
  addRef,
  baselineKeys,
  budgetLine,
  candidateLabel,
  characterLimitNote,
  fullNote,
  groupCandidates,
  hasOverrides,
  imageProblem,
  IMAGE_TYPES,
  nextBaseline,
  pickedKeys,
  redoLabel,
  refKey,
  refsChanged,
  removeRef,
  resetRefs,
  suggestLabel,
  swapRef,
  togglePin,
  warningNotices,
  type Baseline,
} from "../references";
import { bump, toast } from "../store";
import type { AdScene, Asset, ModelInfo, Project, ReferencesView, SetReferencesBody } from "../types";
import { Sheet, Spinner } from "./bits";
import { Fold } from "./Fold";
import { Notice } from "./Notice";
import { openPicker } from "../picker";

type RefPick = ReferencesView["picked"][number];
type Cand = ReferencesView["candidates"][number];
type Open = { mode: "swap"; from: RefPick; step: "candidates" | "library" } | { mode: "add"; step: "candidates" | "library" };

const baseKey = (projectId: string, sceneId: string) => `cao.refs.base.${projectId}/${sceneId}`;
function readBase(k: string): Baseline | null {
  try {
    const v = localStorage.getItem(k);
    return v ? (JSON.parse(v) as Baseline) : null;
  } catch {
    return null;
  }
}
function writeBase(k: string, b: Baseline | null) {
  try {
    if (b) localStorage.setItem(k, JSON.stringify(b));
    else localStorage.removeItem(k);
  } catch {
    /* private mode: "Changed" just isn't remembered */
  }
}

export function References({ p, scene, index, models, frameBusy }: { p: Project; scene: AdScene; index: number; models: ModelInfo[]; frameBusy: boolean }) {
  const shot = p.shots[scene.id];
  const frame = shot?.frame ?? null;
  const [view, setView] = useState<ReferencesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Open | null>(null);
  const bk = baseKey(p.id, scene.id);
  const [base, setBase] = useState<Baseline | null>(() => readBase(bk));
  const live = useRef(0);
  const row = useRef<HTMLOListElement>(null);
  // pins go first: after an add or a swap, show the start of the row
  const toStart = () => row.current?.scrollTo?.({ left: 0, behavior: "smooth" });

  // reload when the shot or its cast could have changed what the picker sends
  const deps = [p.id, scene.id, frame?.assetId ?? "", (shot?.refAssetIds ?? []).join(","), scene.imagePrompt, (scene.characterIds ?? []).join(","), p.updatedAt];
  useEffect(() => {
    const n = ++live.current;
    api
      .references(p.id, scene.id)
      .then((v) => {
        if (n !== live.current) return;
        setView(v);
        setError(null);
      })
      .catch((e: unknown) => {
        if (n !== live.current) return;
        // a scene drawn on screen has no picture, so no references: show nothing
        setError(e instanceof ApiError && e.code === "NO_PICTURE_NEEDED" ? "" : (e as Error).message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => setBase(readBase(bk)), [bk]);

  // remember what the frame was made with (when the api doesn't say), so "Changed" survives a reload
  const current = view ? pickedKeys(view) : null;
  useEffect(() => {
    if (!current) return;
    const next = nextBaseline(frame, base, current);
    if (next !== base) {
      setBase(next);
      writeBase(bk, next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame?.assetId, current?.join("|")]);

  if (error === "") return null;
  if (!view)
    return error ? <Notice tone="error" code="REFS_LOAD" text={`Couldn't load the reference pictures. ${error}`} /> : null;

  const price = models.find((m) => m.id === view.budget.model)?.credits ?? null;
  const changed = !!frame && refsChanged(current ?? [], baselineKeys(frame, base));
  const notices = warningNotices(view);
  const full = view.budget.max > 0 && view.picked.length >= view.budget.max;

  const save = async (body: SetReferencesBody, said: string) => {
    setSaving(true);
    try {
      const v = await api.setReferences(p.id, scene.id, body);
      setView(v);
      director.context(`The user ${said} on scene ${index + 1}'s reference pictures by tapping. The next frame uses: ${v.picked.map((r) => r.label).join("; ") || "none"}.`);
      return true;
    } catch (e) {
      toast(`Couldn't change the references. ${(e as Error).message}`, "bad");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const redo = async () => {
    setSaving(true);
    try {
      // PUT what's on screen, then make the frame from it (the price was on the button)
      const v = await api.setReferences(p.id, scene.id, { pins: view.overrides.pins, removed: view.overrides.removed });
      setView(v);
      const pending: Baseline = { pending: true, prevAssetId: frame?.assetId ?? null, keys: pickedKeys(v) };
      setBase(pending);
      writeBase(bk, pending);
      await act.makeFrame(p.id, scene.id);
    } catch (e) {
      toast(`Couldn't start the frame. ${(e as Error).message}`, "bad");
    } finally {
      setSaving(false);
    }
  };

  const useModel = async (s: NonNullable<(typeof notices)[number]["suggest"]>) => {
    setSaving(true);
    try {
      await api.setProjectModel(p.id, "image", s.modelId);
      setView(await api.references(p.id, scene.id));
      bump("project");
      director.context(`The user switched "${p.title}"'s picture model to ${s.short} (about ${s.credits} credits a picture) from scene ${index + 1}'s references, so more characters fit.`);
    } catch (e) {
      toast(`Couldn't change the model. ${(e as Error).message}`, "bad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="refs" data-scene={scene.id}>
      {notices.map((n, i) => (
        <Notice
          key={`${n.code}-${i}`}
          tone={n.tone}
          code={n.code}
          text={n.text}
          action={
            n.suggest ? (
              <button data-action="refs.model" type="button" className="btn btn-quiet btn-small" disabled={saving} onClick={() => void useModel(n.suggest!)}>
                {suggestLabel(n.suggest)}
              </button>
            ) : undefined
          }
        />
      ))}
      <Fold id="scene.references" className="refs-fold" title="References" summary={budgetLine(view)} price={changed ? price : null}>
        {view.budget.max <= 0 ? (
          <p className="refs-empty">{view.budget.modelShort} draws from the words alone. Pick a picture model that takes references to use your sheets and photos.</p>
        ) : (
          <ol className="refs-row" ref={row} aria-label={`Reference pictures for scene ${index + 1}, in the order they're sent`}>
            {view.picked.map((r, i) => (
              <RefTile
                key={refKey(r)}
                r={r}
                n={i + 1}
                busy={saving}
                onSwap={() => setOpen({ mode: "swap", from: r, step: "candidates" })}
                onPin={() => void save(togglePin(view.overrides, refKey(r)), r.pinned ? `unpinned ${r.label}` : `pinned ${r.label}`)}
                onRemove={() => void save(removeRef(view.overrides, refKey(r)), `took out ${r.label}`)}
              />
            ))}
            <li className="ref-tile ref-tile-add">
              <button data-action="refs.add" type="button" className="ref-add" disabled={saving} onClick={() => setOpen({ mode: "add", step: "candidates" })}>
                <PlusMark />
                <span>Add</span>
              </button>
            </li>
          </ol>
        )}
        <div className="refs-foot">
          <button data-action="world.open" type="button" className="btn btn-quiet btn-small refs-world" onClick={() => openWorld("location")}>
            Places and props
          </button>
          {changed && (
            <>
              <span className="refs-changed">Changed</span>
              <button data-action="refs.redo" type="button" className="btn btn-primary btn-small" disabled={saving || frameBusy || price == null} onClick={() => void redo()} title={price == null ? "Couldn't get the price" : undefined}>
                {redoLabel(price)}
              </button>
            </>
          )}
          {hasOverrides(view.overrides) && (
            <button data-action="refs.reset" type="button" className="btn btn-quiet btn-small" disabled={saving} onClick={() => void save(resetRefs(), "went back to the automatic picks")}>
              Back to automatic
            </button>
          )}
        </div>
      </Fold>
      {open && (
        <RefSheet
          p={p}
          view={view}
          open={open}
          busy={saving}
          full={full}
          onStep={(step) => setOpen({ ...open, step })}
          onClose={() => setOpen(null)}
          onCandidate={async (c) => {
            const body = open.mode === "swap" ? swapRef(view.overrides, open.from, c) : addRef(view.overrides, c.key);
            if (await save(body, open.mode === "swap" ? `swapped ${open.from.label} for ${c.label}` : `added ${c.label}`)) {
              setOpen(null);
              toStart();
            }
          }}
          onAsset={async (a) => {
            const body: SetReferencesBody = open.mode === "swap" ? { ...removeRef(view.overrides, refKey(open.from)), add: [a.id] } : { add: [a.id] };
            if (await save(body, open.mode === "swap" ? `swapped ${open.from.label} for "${a.name}" from the library` : `added "${a.name}" from the library`)) {
              setOpen(null);
              toStart();
            }
          }}
        />
      )}
    </div>
  );
}

function RefTile({ r, n, busy, onSwap, onPin, onRemove }: { r: RefPick; n: number; busy: boolean; onSwap: () => void; onPin: () => void; onRemove: () => void }) {
  return (
    <li className={`ref-tile${r.pinned ? " is-pinned" : ""}`}>
      <button data-action="refs.swap" type="button" className="ref-art" onClick={onSwap} disabled={busy} aria-label={`Swap ${r.label}`}>
        {r.url ? <img src={r.thumbUrl || r.url} alt="" loading="lazy" decoding="async" /> : <span className="ref-blank" />}
        <span className="ref-n" aria-hidden>
          {n}
        </span>
        {r.pinned && (
          <span className="ref-pinned" aria-hidden>
            <PinMark />
          </span>
        )}
      </button>
      <p className="ref-label">{r.label}</p>
      <p className="ref-reason">{r.reason}</p>
      <div className="ref-acts">
        <button data-action="refs.swap" type="button" className="icon-btn" disabled={busy} onClick={onSwap} aria-label={`Swap ${r.label}`} title="Swap">
          <SwapMark />
        </button>
        <button data-action="refs.pin" type="button" className={`icon-btn${r.pinned ? " is-on" : ""}`} disabled={busy} onClick={onPin} aria-pressed={!!r.pinned} aria-label={r.pinned ? `Unpin ${r.label}` : `Pin ${r.label}`} title={r.pinned ? "Unpin" : "Pin"}>
          <PinMark />
        </button>
        <button data-action="refs.remove" type="button" className="icon-btn" disabled={busy} onClick={onRemove} aria-label={`Take out ${r.label}`} title="Take out">
          <CloseMark />
        </button>
      </div>
    </li>
  );
}

/** The bottom sheet: what else could go in (grouped), then the library and uploads. */
function RefSheet({ p, view, open, busy, full, onStep, onClose, onCandidate, onAsset }: { p: Project; view: ReferencesView; open: Open; busy: boolean; full: boolean; onStep: (s: Open["step"]) => void; onClose: () => void; onCandidate: (c: Cand) => void; onAsset: (a: Asset) => void }) {
  const from = open.mode === "swap" ? open.from : null;
  const title = from ? `Swap ${from.label}` : "Add a reference";
  if (open.step === "library") return <LibraryPick p={p} title={from ? `Swap ${from.label}` : "Add from your library"} exclude={view.picked.map((r) => r.assetId)} busy={busy} onBack={() => onStep("candidates")} onClose={onClose} onPick={onAsset} />;
  const groups = groupCandidates(view.candidates, from?.characterId);
  const limit = !from?.characterId ? characterLimitNote(view, null) : null;
  const pushOut = !from && full ? fullNote(view) : null;
  const removed = new Set(view.overrides.removed);
  return (
    <Sheet title={title} onClose={onClose} className="refs-sheet">
      <div className="sheet-body">
        <div className="refs-sheet-top">
          <button type="button" className="btn btn-quiet btn-small" onClick={() => onStep("library")}>
            From your library or upload
          </button>
        </div>
        {limit && <Notice tone="warning" code="CHARACTER_LIMIT" text={limit} />}
        {pushOut && <Notice tone="info" code="REFS_FULL" text={pushOut} />}
        {!groups.length && <p className="muted">Nothing else from this video or its series yet. Add a picture from your library.</p>}
        {groups.map((g) => (
          <section key={g.id} className="refs-group" aria-label={g.title}>
            <h3 className="refs-group-title">{g.title}</h3>
            <ul className="refs-grid">
              {g.items.map((c) => (
                <li key={c.key}>
                  <button type="button" className="refs-cand" disabled={busy} onClick={() => onCandidate(c)}>
                    <span className="refs-cand-art">{c.url ? <img src={c.thumbUrl || c.url} alt="" loading="lazy" decoding="async" /> : <span className="ref-blank" />}</span>
                    <span className="refs-cand-label">{candidateLabel(c, g.id.startsWith("char:"))}</span>
                    {removed.has(c.key) && <span className="refs-cand-tag">Taken out</span>}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <div className="sheet-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose} data-autofocus>
            Not now
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** The library, scoped to this video with "Everything" one tap away, plus "Upload a picture". */
export function LibraryPick({ p, title, exclude, busy, onBack, onClose, onPick }: { p: Project; title: string; exclude: string[]; busy: boolean; onBack: () => void; onClose: () => void; onPick: (a: Asset) => void }) {
  const [scope, setScope] = useState<"here" | "all">("here");
  const [items, setItems] = useState<Asset[] | null>(null);
  const [terms, setTerms] = useState<boolean | null>(null);
  const [ticked, setTicked] = useState(false);
  const [sent, setSent] = useState<number | null>(null);
  // 2026-11-02: while the photo picker is up the grid of pictures is let go (an iPhone dropped the app otherwise)
  const [picking, setPicking] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let on = true;
    setItems(null);
    api
      .library({ kind: "image", ...(scope === "here" ? { scope: `project:${p.id}` } : {}) })
      .then((x) => on && setItems(x.filter((a) => !exclude.includes(a.id))))
      .catch(() => on && setItems([]));
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, p.id]);
  useEffect(() => {
    let on = true;
    api
      .myStorage()
      .then((s) => on && setTerms(s.termsAccepted))
      .catch(() => on && setTerms(false));
    return () => {
      on = false;
    };
  }, []);

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (input.current) input.current.value = "";
    if (!file) return;
    const bad = imageProblem(file);
    if (bad) return toast(bad, "bad");
    if (terms === false && !ticked) return toast("Tick the box first: it says you can use this picture.", "bad");
    setSent(0);
    try {
      const a = await api.uploadFile(file, { kind: "image", scope: { projectId: p.id }, ...(ticked ? { termsAccepted: true } : {}) }, setSent);
      setTerms(true);
      onPick(a);
    } catch (err) {
      if (err instanceof ApiError && err.code === "TERMS_NEEDED") {
        setTerms(false);
        toast("Tick the box first: it says you can use this picture.", "bad");
      } else toast(`Couldn't upload the picture. ${(err as Error).message}`, "bad");
    } finally {
      setSent(null);
    }
  };

  return (
    <Sheet title={title} onClose={onClose} className="refs-sheet">
      <div className="sheet-body">
        <div className="refs-scope" role="group" aria-label="Which pictures">
          <Chip on={scope === "here"} onClick={() => setScope("here")}>
            This video
          </Chip>
          <Chip on={scope === "all"} onClick={() => setScope("all")}>
            Everything
          </Chip>
        </div>
        {picking && <p className="muted">Choosing a picture…</p>}
        {!items && !picking && <Spinner label="Loading your pictures" />}
        {items && !items.length && <p className="muted">{scope === "here" ? "No pictures saved with this video yet." : "No pictures in your library yet."} Upload one below.</p>}
        {!!items?.length && !picking && (
          <ul className="photos" aria-label="Your pictures">
            {items.map((a) => (
              <li key={a.id}>
                <button type="button" className="photo" disabled={busy || sent !== null} onClick={() => onPick(a)} aria-label={`Use ${a.name}`}>
                  {a.thumbUrl || a.url ? <img src={a.thumbUrl || a.url || ""} alt="" loading="lazy" decoding="async" /> : <span className="photo-blank" />}
                </button>
              </li>
            ))}
          </ul>
        )}
        {terms === false && (
          <label className="check refs-terms">
            <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
            <span>
              {UPLOAD_TERMS_CONSENT.label}{" "}
              <a href={UPLOAD_TERMS_CONSENT.link.href} target="_blank" rel="noopener noreferrer">
                {UPLOAD_TERMS_CONSENT.link.text}
              </a>
            </span>
          </label>
        )}
        <input ref={input} type="file" accept={IMAGE_TYPES.join(",")} hidden onChange={(e) => void upload(e)} />
        <div className="sheet-actions">
          <button type="button" className="btn btn-quiet" onClick={onBack}>
            Back
          </button>
          <button type="button" className="btn btn-quiet" disabled={sent !== null || (terms === false && !ticked)} onClick={() => openPicker(input.current, setPicking)}>
            {sent === null ? "Upload a picture" : sent >= 1 ? "Finishing up" : `Uploading ${Math.round(sent * 100)} %`}
          </button>
        </div>
        <p className="field-hint">PNG, JPEG or WebP, up to 20 MB. It's saved with this video.</p>
      </div>
    </Sheet>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`chip${on ? " is-on" : ""}`} aria-pressed={on} onClick={onClick}>
      {children}
    </button>
  );
}

const svg = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true, focusable: "false" as const };
const PinMark = () => (
  <svg {...svg}>
    <path d="M9 4h6l-1 5 3 3v2H7v-2l3-3zM12 14v6" />
  </svg>
);
const SwapMark = () => (
  <svg {...svg}>
    <path d="M7 7h11l-3-3M17 17H6l3 3" />
  </svg>
);
const CloseMark = () => (
  <svg {...svg}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const PlusMark = () => (
  <svg {...svg} width={20} height={20}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
